<template>
  <div class="container page-top contact">
    <div class="intro">
      <h1 class="h-page title">{{ t('closing.title') }}</h1>
      <p class="lead">{{ t('closing.sub') }}</p>
      <div class="email-block">
        <span class="email-label">{{ t('contact.or_email') }}</span>
        <a class="email" :href="`mailto:${contactEmail}`">{{ contactEmail }}</a>
      </div>
    </div>

    <div id="contact-form" class="card">
      <div v-if="sent" class="sent" role="status">
        <span class="check" aria-hidden="true">✓</span>
        <h2 class="sent-title">{{ t('contact.sent_title') }}</h2>
        <p class="sent-sub">{{ t('contact.sent_sub') }}</p>
      </div>

      <form v-else class="form" novalidate @submit.prevent="submit">
        <div class="two-up">
          <label class="field">
            <span>{{ t('contact.f_name') }} *</span>
            <input v-model.trim="form.name" required autocomplete="name" :class="{ invalid: showErrors && !form.name }">
          </label>
          <label class="field">
            <span>{{ t('contact.f_company') }}</span>
            <input v-model.trim="form.company" autocomplete="organization">
          </label>
          <label class="field">
            <span>{{ t('contact.f_email') }} *</span>
            <input v-model.trim="form.email" required type="email" autocomplete="email" dir="ltr" :class="{ invalid: showErrors && !emailOk }">
          </label>
          <label class="field">
            <span>{{ t('contact.f_phone') }}</span>
            <input v-model.trim="form.phone" type="tel" autocomplete="tel" dir="ltr">
          </label>
        </div>

        <fieldset class="field interest">
          <legend>{{ t('contact.f_interest') }}</legend>
          <div class="chips">
            <button
              v-for="chip in chips"
              :key="chip.value"
              type="button"
              class="chip"
              :class="{ on: selected.includes(chip.value) }"
              :aria-pressed="selected.includes(chip.value)"
              @click="toggle(chip.value)"
            >{{ chip.label }}</button>
          </div>
        </fieldset>

        <label class="field">
          <span>{{ t('contact.f_tools') }}</span>
          <input v-model.trim="form.tools">
        </label>
        <label class="field">
          <span>{{ t('contact.f_msg') }}</span>
          <textarea v-model.trim="form.message" rows="4"></textarea>
        </label>

        <!-- Honeypot: invisible to people, bots fill it in. -->
        <input v-model="form.website" class="hp" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true">

        <p v-if="error" class="error" role="alert">
          {{ error }}
          <a :href="`mailto:${contactEmail}`">{{ contactEmail }}</a>
        </p>

        <button type="submit" class="send" :disabled="sending">{{ t('contact.f_submit') }}{{ sending ? ' …' : '' }}</button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useLocale } from '../composables/useLocale'
import { submitContact, ApiError } from '../services/api'
import { solutions } from '../data'

const route = useRoute()
const { locale, t, pick, contactEmail } = useLocale()

const form = reactive({ name: '', company: '', email: '', phone: '', tools: '', message: '', website: '' })

const chips = computed(() => [
  ...solutions.map((solution) => ({ value: solution.slug, label: pick(solution, 'name') })),
  { value: 'other', label: t('contact.other') }
])

// ?solution=garage-crm (or several, comma separated) pre-selects chips.
const known = new Set([...solutions.map((solution) => solution.slug), 'other'])
const selected = ref([])

// Applied after mount: the page is prerendered without a query string, so
// reading it during setup would not match the server HTML when hydrating.
onMounted(() => {
  const fromQuery = [].concat(route.query.solution || [])
    .flatMap((value) => String(value).split(','))
    .map((value) => value.trim())
    .filter((value) => known.has(value))

  selected.value = [...new Set(fromQuery)]
})

function toggle (value) {
  selected.value = selected.value.includes(value)
    ? selected.value.filter((item) => item !== value)
    : [...selected.value, value]
}

const emailOk = computed(() => /^\S+@\S+\.\S+$/.test(form.email))

const sending = ref(false)
const sent = ref(false)
const error = ref('')
const showErrors = ref(false)

async function submit () {
  showErrors.value = true
  error.value = ''

  if (!form.name || !emailOk.value) {
    return
  }

  sending.value = true

  try {
    await submitContact({
      name: form.name,
      company: form.company || undefined,
      email: form.email,
      phone: form.phone || undefined,
      solutions: selected.value.length ? selected.value : undefined,
      tools: form.tools || undefined,
      message: form.message || undefined,
      locale: locale.value,
      website: form.website
    })
    sent.value = true
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } catch (failure) {
    const first = failure instanceof ApiError && Object.values(failure.errors || {})[0]
    error.value = (Array.isArray(first) ? first[0] : '') || t('contact.error')
  } finally {
    sending.value = false
  }
}
</script>

<style scoped>
#contact-form {
  scroll-margin-top: 96px;
}

.contact {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 440px), 1fr));
  gap: clamp(40px, 6vw, 80px);
  align-items: start;
  padding-bottom: var(--section-gap);
}

.intro {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.title {
  font-size: clamp(48px, 7vw, 96px);
  line-height: 1.02;
}

.intro .lead {
  max-width: 42ch;
}

.email-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 24px;
  border-top: 1px solid var(--divider);
}

.email-label {
  font-size: 14px;
  color: var(--tertiary);
}

.email {
  font-family: var(--font-display);
  font-size: clamp(22px, 2.4vw, 30px);
  overflow-wrap: anywhere;
}

.card {
  background: var(--surface);
  border-radius: 28px;
  padding: clamp(24px, 4vw, 44px);
}

.form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.two-up {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  gap: 14px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
}

.field legend {
  padding: 0;
  margin-bottom: 10px;
}

input:not(.hp),
textarea {
  font-size: 16px;
  font-weight: 400;
  padding: 15px 18px;
  border-radius: var(--radius-input);
  border: 1px solid var(--input-border);
  background: #fff;
  color: var(--ink);
  width: 100%;
  outline: none;
}

input:not(.hp):focus,
textarea:focus {
  border-color: var(--ink);
  box-shadow: 0 0 0 3px rgba(15, 27, 45, 0.12);
}

input.invalid {
  border-color: #d93a3a;
}

textarea {
  resize: vertical;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  font-size: 14px;
  cursor: pointer;
  padding: 9px 14px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--input-border);
  background: #fff;
  color: var(--ink);
  transition: background 0.15s ease, border-color 0.15s ease;
}

.chip:hover {
  border-color: var(--ink);
}

.chip.on {
  background: var(--ink);
  border-color: var(--ink);
  color: #fff;
}

.hp {
  position: absolute;
  inset-inline-start: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.error {
  font-size: 15px;
  color: #b42318;
  line-height: 1.5;
}

.send {
  margin-top: 6px;
  border: none;
  border-radius: var(--radius-pill);
  background: var(--primary);
  color: var(--ink);
  font-size: 17px;
  font-weight: 500;
  padding: 18px 30px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.send:hover:not(:disabled) {
  background: var(--primary-hover);
}

.send:disabled {
  opacity: 0.7;
  cursor: progress;
}

.sent {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0;
}

.check {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--primary);
  color: var(--ink);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.sent-title {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: 36px;
  line-height: 1.2;
}

.sent-sub {
  font-size: 17px;
  color: var(--muted);
}
</style>
